import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ToolService } from '../../services/tool.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';
import { combineLatest, Subscription } from 'rxjs';
import { UserFormComponent } from '../user-form/user-form.component';
import { NodeItem, User, UserType } from '../../api/models';
import { StateService } from '../../services/state.service';

@Component({
  selector: 'lib-user-overview',
  templateUrl: './user-overview.component.html',
  styleUrls: ['./user-overview.component.scss'],
  standalone: false,
})
export class UserOverviewComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);

  public isDeleteAlertOpen: boolean = false;
  public visibleUsers: User[] = [];
  public activeCount: number = 0;
  public deactivatedCount: number = 0;
  public deletedCount: number = 0;

  private allUsers: User[] = [];
  private searchTerm: string = '';
  private sub: Subscription;
  private profileImageUrls: Record<string, string> = {};

  constructor(
    public state: StateService,
    private modalController: ModalController,
    private tools: ToolService
  ) {}

  ngOnInit() {
    this.sub = combineLatest([
      this.state.users$,
      this.state.nodeItems$,
    ]).subscribe(([users, nodeItems]) => {
      this.allUsers = users ?? [];
      this.loadProfileImages(nodeItems);
      this.filterEntries();
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  public handleSearchInput(event: any) {
    const term = event.target.value as string;
    this.searchTerm = (term ?? '').trim().toLocaleLowerCase();
    this.filterEntries();
  }

  public getUserTypeLabel(type: UserType): string {
    switch (type) {
      case UserType.$0:
        return this.translate.instant('user.type.employee');
      case UserType.$1:
        return this.translate.instant('user.type.office');
      case UserType.$2:
        return this.translate.instant('user.type.companyAdmin');
      default:
        return this.translate.instant('user.type.unknown');
    }
  }

  public getProfileImageUrl(user: User): string | null {
    return this.profileImageUrls[user.userName] ?? null;
  }

  public wasSeenToday(user: User): boolean {
    if (!user.lastSeenUtc) return false;
    return this.tools.dateStringsAreSameDay(
      user.lastSeenUtc,
      new Date().toISOString()
    );
  }

  public async editUser(user: User) {
    if (user.isDeleted) return;
    const modal = await this.modalController.create({
      component: UserFormComponent,
      componentProps: {
        userToEdit: user,
      },
    });
    await modal.present();
  }

  private filterEntries() {
    this.deletedCount = this.allUsers?.filter((user) => user.isDeleted).length;
    this.deactivatedCount = this.allUsers?.filter(
      (user) => !user.isDeleted && user.isDeactivated
    ).length;
    this.activeCount = this.allUsers?.filter(
      (user) => !user.isDeleted && !user.isDeactivated
    ).length;

    let items = this.allUsers ?? [];

    if (this.searchTerm) {
      items = items.filter((user) =>
        `${user.firstName} ${user.lastName}`
          .toLocaleLowerCase()
          .includes(this.searchTerm)
      );
    }

    const byName = (a: User, b: User) =>
      `${a.firstName} ${a.lastName}`.localeCompare(
        `${b.firstName} ${b.lastName}`
      );

    // aktive/deaktivierte zuerst, gelöschte immer ganz am Ende, jeweils alphabetisch
    this.visibleUsers = [...items]?.sort((a, b) => {
      if (a.isDeleted !== b.isDeleted) return a.isDeleted ? 1 : -1;
      return byName(a, b);
    });
  }

  // lädt Profilbilder aus vorhandenen NodeItems (Platzhalter, falls keins hinterlegt ist)
  private loadProfileImages(nodeItems: NodeItem[]) {
    this.allUsers?.forEach((user) => {
      const profilePicture = nodeItems.find(
        (n) => n.userId === user.userName && n.isProfilePicture
      );
      if (!profilePicture) return;
      this.state.downloadPreviewImage(profilePicture.id).subscribe((blob) => {
        this.profileImageUrls[user.userName] = URL.createObjectURL(blob);
      });
    });
  }
}
